import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { updatePageMeta } from '../utils/seo';

// Constants
const STORAGE_KEY = 'trip-split-data';
const GUEST_STORAGE_PREFIX = 'trip-split-guest-';
const UNLOCK_KEY = 'trip-split-unlocked';
const ADMIN_CODE = process.env.REACT_APP_TRIPSPLIT_ADMIN_CODE || 'admin';

function generateTripCode() {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

function generatePasscode() {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  let code = '';
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}
const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP'];
const RATES_API = 'https://api.frankfurter.app/latest';
const EXPENSE_CATEGORIES = [
  { id: 'food', label: 'Food', icon: 'restaurant' },
  { id: 'transport', label: 'Transportation', icon: 'directions_bus' },
  { id: 'activity', label: 'Activity', icon: 'local_activity' },
  { id: 'hotel', label: 'Hotel', icon: 'hotel' },
  { id: 'shopping', label: 'Shopping', icon: 'shopping_bag' },
  { id: 'other', label: 'Other', icon: 'receipt_long' },
];

const DEFAULT_CURRENCY = 'CAD';
const FONT_FAMILY = '"Manrope", system-ui, -apple-system, sans-serif';

// Design system
const colors = {
  bg: '#f5f5f7',
  card: '#ffffff',
  primary: '#0a84ff',
  accent: '#ff3b30',
  text: '#1d1d1f',
  muted: '#6e6e73',
  border: '#e5e5ea',
};

const shadows = {
  card: '0 10px 30px rgba(0,0,0,0.06)',
  button: '0 6px 16px rgba(10,132,255,0.25)',
  buttonAccent: '0 6px 16px rgba(255,59,48,0.25)',
  expense: '0 10px 28px rgba(0,0,0,0.06)',
  tooltip: '0 8px 24px rgba(0,0,0,0.15)',
};

// Utility functions
function decodeState(encoded) {
  try {
    return JSON.parse(atob(decodeURIComponent(encoded)));
  } catch {
    return null;
  }
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function getTodayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateISO(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDateRange(startIso, endIso) {
  if (!startIso && !endIso) return '';
  if (startIso && !endIso) return formatDateISO(startIso);
  if (!startIso && endIso) return formatDateISO(endIso);
  if (startIso === endIso) return formatDateISO(startIso);

  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${formatDateISO(startIso)} — ${formatDateISO(endIso)}`;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) {
    const month = start.toLocaleDateString('en-US', { month: 'long' });
    return `${month} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }

  const startLabel = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const endLabel = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  return `${startLabel} — ${endLabel}, ${end.getFullYear()}`;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function round4(n) {
  return Math.round(n * 10000) / 10000;
}

function evaluateMathExpression(str) {
  try {
    // Remove commas and trim
    const cleaned = String(str).replace(/,/g, '').trim();
    // Only allow numbers, basic operators, parentheses, and decimal points
    if (!/^[0-9+\-*/().\s]+$/.test(cleaned)) {
      return null;
    }
    // Simple safe evaluation using eval in a controlled way
    // eslint-disable-next-line no-eval
    const result = eval(cleaned);
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}

function toTrip(amount, currency, tripCurrency, ratesToTrip) {
  if (!amount || !currency) return amount || 0;
  const rate = (ratesToTrip && ratesToTrip[currency]) ?? (currency === tripCurrency ? 1 : 1);
  return (Number(amount) || 0) * rate;
}

// Business logic
function computeSettleUp(people, expenses, tripCurrency, ratesToTrip, groups = []) {
  const net = {};
  people.forEach((p) => (net[p] = 0));
  const trip = tripCurrency || DEFAULT_CURRENCY;
  const rates = ratesToTrip || { [trip]: 1 };

  // Calculate individual balances
  expenses.forEach((exp) => {
    const expCurrency = exp.currency || trip;
    const amt = toTrip(Number(exp.amount) || 0, expCurrency, trip, rates);
    if (amt <= 0) return;

    let shares = {};
    if ((exp.splitType === 'custom' || exp.splitType === 'customTax') && exp.customAmounts && typeof exp.customAmounts === 'object') {
      Object.entries(exp.customAmounts).forEach(([p, v]) => {
        const n = toTrip(Number(v) || 0, expCurrency, trip, rates);
        if (n > 0) shares[p] = n;
      });
    } else {
      const who = exp.splitBetween && exp.splitBetween.length > 0 ? exp.splitBetween : people;
      const share = who.length > 0 ? amt / who.length : 0;
      who.forEach((p) => (shares[p] = share));
    }

    const who = Object.keys(shares);
    const paidBy = exp.paidBy || (who.length > 0 ? who[0] : null);
    if (!paidBy) return;

    if (!net[paidBy]) net[paidBy] = 0;
    net[paidBy] = round2((net[paidBy] || 0) + amt);
    who.forEach((p) => {
      net[p] = round2((net[p] || 0) - (Number(shares[p]) || 0));
    });
  });

  // Aggregate by groups
  const groupNet = {};
  groups.forEach((g) => {
    groupNet[g.id] = 0;
    g.people.forEach((p) => {
      groupNet[g.id] = round2((groupNet[g.id] || 0) + (net[p] || 0));
    });
  });

  // Create entities (groups and individuals)
  const entities = [];
  const usedPeople = new Set();

  groups.forEach((g) => {
    entities.push({
      id: g.id,
      name: g.people.join(' / '),
      isGroup: true,
      people: g.people,
      balance: groupNet[g.id] || 0,
    });
    g.people.forEach((p) => usedPeople.add(p));
  });

  people.forEach((p) => {
    if (!usedPeople.has(p)) {
      entities.push({
        id: p,
        name: p,
        isGroup: false,
        people: [p],
        balance: net[p] || 0,
      });
    }
  });

  Object.keys(net).forEach((p) => {
    if (!usedPeople.has(p) && !people.includes(p)) {
      entities.push({
        id: p,
        name: p,
        isGroup: false,
        people: [p],
        balance: net[p] || 0,
      });
    }
  });

  // Calculate transactions
  const creditors = entities.filter((e) => e.balance > 0).sort((a, b) => b.balance - a.balance);
  const debtors = entities.filter((e) => e.balance < 0).sort((a, b) => a.balance - b.balance);
  const transactions = [];

  let i = 0, j = 0;
  while (i < creditors.length && j < debtors.length) {
    const c = creditors[i];
    const d = debtors[j];
    const amount = round2(Math.min(c.balance, -d.balance));
    if (amount <= 0) break;
    transactions.push({ from: d.name, to: c.name, amount, fromEntity: d, toEntity: c });
    c.balance = round2(c.balance - amount);
    d.balance = round2(d.balance + amount);
    if (c.balance <= 0) i++;
    if (d.balance >= 0) j++;
  }

  return transactions;
}

function calculatePeopleSpend(people, expenses, tripCurrency, ratesToTrip) {
  const peopleShares = {};
  people.forEach((p) => (peopleShares[p] = 0));

  expenses.forEach((exp) => {
    const expCurrency = exp.currency || tripCurrency;
    const amt = toTrip(Number(exp.amount) || 0, expCurrency, tripCurrency, ratesToTrip);
    if (amt <= 0) return;

    let shares = {};
    if ((exp.splitType === 'custom' || exp.splitType === 'customTax') && exp.customAmounts && typeof exp.customAmounts === 'object') {
      Object.entries(exp.customAmounts).forEach(([p, v]) => {
        const n = toTrip(Number(v) || 0, expCurrency, tripCurrency, ratesToTrip);
        if (n > 0) shares[p] = n;
      });
    } else {
      const who = exp.splitBetween && exp.splitBetween.length > 0 ? exp.splitBetween : people;
      const share = who.length > 0 ? amt / who.length : 0;
      who.forEach((p) => (shares[p] = share));
    }

    Object.entries(shares).forEach(([p, v]) => {
      if (peopleShares[p] != null) {
        peopleShares[p] = round2((peopleShares[p] || 0) + (Number(v) || 0));
      }
    });
  });

  return Object.entries(peopleShares)
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount);
}

function defaultState() {
  return {
    people: [],
    groups: [],
    expenses: [],
    currency: DEFAULT_CURRENCY,
    ratesToTrip: { CAD: 1, USD: 1.35 },
    tripStartDate: getTodayISO(),
    tripEndDate: getTodayISO(),
    showUsd: true,
    passcode: '',
  };
}

function buildTripData(source) {
  const trip = source?.currency || DEFAULT_CURRENCY;
  return {
    people: Array.isArray(source?.people) ? source.people : [],
    expenses: Array.isArray(source?.expenses) ? source.expenses : [],
    currency: trip,
    ratesToTrip: source?.ratesToTrip && typeof source.ratesToTrip === 'object' ? source.ratesToTrip : { [trip]: 1 },
    tripStartDate: source?.tripStartDate || source?.tripDate || getTodayISO(),
    tripEndDate: source?.tripEndDate || source?.tripDate || getTodayISO(),
    showUsd: source?.showUsd !== false,
    passcode: typeof source?.passcode === 'string' ? source.passcode : '',
  };
}

function createEmptyExpense() {
  return {
    name: '',
    emoji: 'restaurant',
    date: getTodayISO(),
    amount: '',
    currency: '',
    splitBetween: [],
    customAmounts: {},
    autoSplit: true,
    paidBy: '',
    customTaxMode: false,
    items: [],
    taxAmount: 0,
    tipAmount: 0,
    notes: '',
  };
}

// Trip backend (Supabase) - syncs trip data by code so admin and guests see the same data
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const BACKEND_ENABLED = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

async function fetchTripFromBackend(tripCode) {
  if (!BACKEND_ENABLED) return null;
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/trip_splits?trip_code=eq.${encodeURIComponent(tripCode)}&select=data,name,updated_at`, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        Accept: 'application/json',
      },
    });
    if (!res.ok) return null;
    const rows = await res.json();
    const row = rows && rows[0];
    if (!row || !row.data) return null;
    return { data: row.data, name: row.name || 'Shared Trip' };
  } catch (_) {
    return null;
  }
}

async function saveTripToBackend(tripCode, tripData, tripName) {
  if (!BACKEND_ENABLED) return;
  try {
    await fetch(`${SUPABASE_URL}/rest/v1/trip_splits`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates',
      },
      body: JSON.stringify({
        trip_code: tripCode,
        data: tripData,
        name: tripName || 'Shared Trip',
        updated_at: new Date().toISOString(),
      }),
    });
  } catch (_) {}
}

// Reusable UI Components
function Button({ children, onClick, variant = 'primary', className = '', disabled, ...props }) {
  const baseStyles = 'rounded-full px-4 py-2 text-sm font-semibold transition hover:opacity-90 disabled:opacity-50';
  const variants = {
    primary: `text-white ${baseStyles}`,
    secondary: `border ${baseStyles}`,
    text: `px-3 py-2 text-sm font-semibold transition hover:opacity-80`,
  };

  const styleMap = {
    primary: { background: colors.primary, boxShadow: shadows.button },
    secondary: { borderColor: colors.border, color: colors.text, background: colors.bg },
    text: { color: colors.primary },
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${variants[variant]} ${className}`}
      style={styleMap[variant]}
      {...props}
    >
      {children}
    </button>
  );
}

function Card({ children, className = '', style = {} }) {
  return (
    <div
      className={`rounded-2xl border ${className}`}
      style={{
        borderColor: colors.border,
        background: colors.card,
        boxShadow: shadows.card,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', className = '', style = {}, ...props }) {
  const baseClass = 'rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2';
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${baseClass} ${className}`}
      style={{
        borderColor: colors.border,
        background: colors.bg,
        color: colors.text,
        ...style,
      }}
      {...props}
    />
  );
}

// Main Component
export default function TripSplit() {
  const [searchParams] = useSearchParams();
  const [unlockRole, setUnlockRole] = useState(() => {
    const v = sessionStorage.getItem(UNLOCK_KEY);
    if (v === 'admin') return 'admin';
    if (v && v.startsWith('guest:')) return { guest: v.slice(6) };
    return null;
  });
  const [pendingGuestData, setPendingGuestData] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [state, setState] = useState(defaultState);
  const [trips, setTrips] = useState([]);
  const [activeTripId, setActiveTripId] = useState(null);

  const unlocked = unlockRole !== null;
  const isAdmin = unlockRole === 'admin';
  const guestShareCode = unlockRole && typeof unlockRole === 'object' && unlockRole.guest ? unlockRole.guest : null;
  const [isEditingTripName, setIsEditingTripName] = useState(false);
  const [tripNameDraft, setTripNameDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editExpense, setEditExpense] = useState(createEmptyExpense());
  const [fetchRatesLoading, setFetchRatesLoading] = useState(false);
  const [fetchRatesError, setFetchRatesError] = useState(null);
  const [expandedExpenses, setExpandedExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState(createEmptyExpense());

  useEffect(() => {
    updatePageMeta(
      'Trip Split',
      'Split trip expenses and bills with friends. Add people, expenses, and see who owes whom.'
    );
  }, []);

  const persistTrips = useCallback((nextTrips, nextActiveTripId) => {
    try {
      const activeTrip = nextTrips.find((t) => t.id === nextActiveTripId) || nextTrips[0];
      const shareCode = activeTrip?.shareCode;

      if (guestShareCode) {
        if (activeTrip) {
          localStorage.setItem(GUEST_STORAGE_PREFIX + guestShareCode, JSON.stringify({ tripData: activeTrip.data, tripName: activeTrip.name }));
          if (BACKEND_ENABLED && shareCode) {
            saveTripToBackend(shareCode, activeTrip.data, activeTrip.name);
          }
        }
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 2, trips: nextTrips, activeTripId: nextActiveTripId }));
        if (BACKEND_ENABLED && activeTrip && shareCode) {
          saveTripToBackend(shareCode, activeTrip.data, activeTrip.name);
        }
      }
    } catch (_) {}
  }, [guestShareCode]);

  // Pending guest data from URL (wait for code before showing trip)
  useEffect(() => {
    const codeParam = searchParams.get('code');
    if (codeParam) {
      setPendingGuestData({ merged: null, shareCode: codeParam });
      return;
    }
    const dataParam = searchParams.get('data');
    if (!dataParam) {
      setPendingGuestData(null);
      return;
    }
    const decoded = decodeState(dataParam);
    if (decoded && (decoded.people || decoded.expenses) && decoded.shareCode) {
      const merged = buildTripData(decoded);
      setPendingGuestData({ merged, shareCode: decoded.shareCode });
    } else {
      setPendingGuestData(null);
    }
  }, [searchParams]);

  // Load admin trips from localStorage (only when unlocked as admin)
  useEffect(() => {
    if (!unlocked || !isAdmin || pendingGuestData) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.trips)) {
          const nextTrips = parsed.trips.map((t) => {
            const data = t.data || {};
            const passcode = (data.passcode && typeof data.passcode === 'string') ? data.passcode : generatePasscode();
            return { ...t, shareCode: t.shareCode || generateTripCode(), data: { ...data, passcode } };
          });
          const nextActiveId = parsed.activeTripId || nextTrips[0]?.id || null;
          const activeTrip = nextTrips.find((t) => t.id === nextActiveId) || nextTrips[0];
          setTrips(nextTrips);
          setActiveTripId(activeTrip?.id || null);
          setState(activeTrip?.data ? buildTripData(activeTrip.data) : defaultState());
        } else if (parsed && (parsed.people || parsed.expenses)) {
          const migratedTripId = generateId();
          const migrated = buildTripData(parsed);
          migrated.passcode = generatePasscode();
          const nextTrips = [{ id: migratedTripId, name: 'Trip 1', data: migrated, shareCode: generateTripCode() }];
          setTrips(nextTrips);
          setActiveTripId(migratedTripId);
          setState(migrated);
          persistTrips(nextTrips, migratedTripId);
        }
      }
    } catch (_) {}
  }, [unlocked, isAdmin, pendingGuestData, persistTrips]);

  // Load guest trip from backend or localStorage (when unlocked as guest, e.g. after refresh)
  useEffect(() => {
    if (!unlocked || !guestShareCode || trips.length > 0) return;
    let cancelled = false;
    (async () => {
      if (BACKEND_ENABLED) {
        const fromBackend = await fetchTripFromBackend(guestShareCode);
        if (cancelled) return;
        if (fromBackend && fromBackend.data) {
          const merged = buildTripData(fromBackend.data);
          const sharedTripId = generateId();
          setTrips([{ id: sharedTripId, name: fromBackend.name || 'Shared Trip', data: merged, shareCode: guestShareCode }]);
          setActiveTripId(sharedTripId);
          setState(merged);
          return;
        }
      }
      try {
        const raw = localStorage.getItem(GUEST_STORAGE_PREFIX + guestShareCode);
        if (raw) {
          const parsed = JSON.parse(raw);
          const merged = buildTripData(parsed.tripData || parsed);
          const sharedTripId = generateId();
          setTrips([{ id: sharedTripId, name: parsed.tripName || 'Shared Trip', data: merged, shareCode: guestShareCode }]);
          setActiveTripId(sharedTripId);
          setState(merged);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [unlocked, guestShareCode, trips.length]);

  const activeTrip = trips.find((trip) => trip.id === activeTripId) || null;

  // Admin: fetch latest trip from backend when viewing a trip (so guest edits show up)
  const refreshActiveTripFromBackend = useCallback(async () => {
    if (!BACKEND_ENABLED || !activeTrip?.shareCode) return;
    const fromBackend = await fetchTripFromBackend(activeTrip.shareCode);
    if (fromBackend && fromBackend.data) {
      const merged = buildTripData(fromBackend.data);
      setState(merged);
      setTrips((prev) =>
        prev.map((t) => (t.id === activeTripId ? { ...t, data: merged, name: fromBackend.name || t.name } : t))
      );
    }
  }, [activeTripId, activeTrip?.shareCode]);

  useEffect(() => {
    if (!BACKEND_ENABLED || !unlocked || !isAdmin || !activeTrip?.shareCode) return;
    refreshActiveTripFromBackend();
  }, [unlocked, isAdmin, activeTripId, activeTrip?.shareCode, refreshActiveTripFromBackend]);

  useEffect(() => {
    if (!BACKEND_ENABLED || !unlocked || !isAdmin) return;
    const onFocus = () => refreshActiveTripFromBackend();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [unlocked, isAdmin, refreshActiveTripFromBackend]);

  const save = useCallback((next) => {
    setState((prev) => {
      const s = typeof next === 'function' ? next(prev) : next;
      if (activeTripId) {
        setTrips((prevTrips) =>
          prevTrips.map((trip) => (trip.id === activeTripId ? { ...trip, data: s } : trip))
        );
      }
      return s;
    });
  }, [activeTripId]);

  useEffect(() => {
    if (trips.length > 0) {
      persistTrips(trips, activeTripId);
    }
  }, [activeTripId, persistTrips, trips]);

  const createTrip = () => {
    const name = `Trip ${trips.length + 1}`;
    const id = generateId();
    const shareCode = generateTripCode();
    const data = { ...defaultState(), passcode: generatePasscode() };
    const nextTrips = [...trips, { id, name, data, shareCode }];
    setTrips(nextTrips);
    setActiveTripId(id);
    setState(data);
    setIsEditingTripName(false);
    setTripNameDraft(name);
    setSettingsOpen(true);
  };

  const selectTrip = (id) => {
    const next = trips.find((trip) => trip.id === id);
    if (!next) return;
    setActiveTripId(id);
    setState(buildTripData(next.data));
    setIsEditingTripName(false);
    setTripNameDraft(next.name || '');
  };

  const saveTripName = () => {
    if (!activeTripId) return;
    const name = (tripNameDraft || '').trim() || activeTrip?.name || 'Untitled trip';
    setTrips((prev) => prev.map((trip) => (trip.id === activeTripId ? { ...trip, name } : trip)));
    setIsEditingTripName(false);
  };

  const setTripDates = (next) => {
    save((s) => ({ ...s, ...next }));
  };

  const deleteTrip = () => {
    if (!activeTripId) return;
    const nextTrips = trips.filter((trip) => trip.id !== activeTripId);
    const nextActive = nextTrips[0]?.id || null;
    setTrips(nextTrips);
    setActiveTripId(nextActive);
    setState(nextTrips[0]?.data ? buildTripData(nextTrips[0].data) : defaultState());
    setIsEditingTripName(false);
    setTripNameDraft(nextTrips[0]?.name || '');
    setSettingsOpen(false);
  };

  const getShareableLink = useCallback(() => {
    const shareCode = activeTrip?.shareCode || '';
    const base = `${window.location.origin}/`;
    const hash = '#/trip-split';
    return shareCode ? `${base}?code=${encodeURIComponent(shareCode)}${hash}` : `${base}${hash}`;
  }, [activeTrip?.shareCode]);

  const copyShareableLink = () => {
    const url = getShareableLink();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchRates = useCallback(() => {
    const trip = state.currency || DEFAULT_CURRENCY;
    const to = CURRENCIES.filter((c) => c !== trip).join(',');
    if (!to) {
      save((s) => ({ ...s, ratesToTrip: { [trip]: 1 } }));
      return;
    }
    setFetchRatesLoading(true);
    setFetchRatesError(null);
    fetch(`${RATES_API}?from=${encodeURIComponent(trip)}&to=${encodeURIComponent(to)}`)
      .then((res) => res.json())
      .then((data) => {
        const next = { [trip]: 1 };
        if (data.rates && typeof data.rates === 'object') {
          Object.entries(data.rates).forEach(([c, r]) => {
            const v = Number(r);
            if (v > 0) next[c] = round4(1 / v);
          });
        }
        save((s) => ({ ...s, ratesToTrip: { ...(s.ratesToTrip || {}), ...next } }));
      })
      .catch((err) => setFetchRatesError(err?.message || 'Could not fetch rates'))
      .finally(() => setFetchRatesLoading(false));
  }, [state.currency, save]);

  const addPerson = (name) => {
    const n = (name || '').trim();
    if (!n) return;
    save((s) => ({
      ...s,
      people: s.people.includes(n) ? s.people : [...s.people, n],
    }));
  };

  const removePerson = (name) => {
    const inUse = (state.expenses || []).some(
      (e) =>
        (e.splitBetween && e.splitBetween.includes(name)) ||
        (e.customAmounts && Object.keys(e.customAmounts || {}).includes(name))
    );
    if (inUse) return;
    save((s) => {
      const updatedGroups = (s.groups || [])
        .map((g) => ({ ...g, people: g.people.filter((p) => p !== name) }))
        .filter((g) => g.people.length >= 2);
      return { ...s, people: s.people.filter((p) => p !== name), groups: updatedGroups };
    });
  };

  const createGroup = (person1, person2) => {
    if (person1 === person2) return;
    save((s) => {
      const groups = s.groups || [];
      const existingGroup1 = groups.find((g) => g.people.includes(person1));
      const existingGroup2 = groups.find((g) => g.people.includes(person2));

      if (existingGroup1 && existingGroup2 && existingGroup1.id === existingGroup2.id) {
        return s;
      }

      if (existingGroup1 && existingGroup2) {
        const mergedPeople = [...new Set([...existingGroup1.people, ...existingGroup2.people])];
        const otherGroups = groups.filter((g) => g.id !== existingGroup1.id && g.id !== existingGroup2.id);
        return { ...s, groups: [...otherGroups, { id: generateId(), people: mergedPeople }] };
      }

      if (existingGroup1) {
        const updated = groups.map((g) =>
          g.id === existingGroup1.id ? { ...g, people: [...new Set([...g.people, person2])] } : g
        );
        return { ...s, groups: updated };
      }

      if (existingGroup2) {
        const updated = groups.map((g) =>
          g.id === existingGroup2.id ? { ...g, people: [...new Set([...g.people, person1])] } : g
        );
        return { ...s, groups: updated };
      }

      return { ...s, groups: [...groups, { id: generateId(), people: [person1, person2] }] };
    });
  };

  const removeGroup = (groupId) => {
    save((s) => ({ ...s, groups: (s.groups || []).filter((g) => g.id !== groupId) }));
  };

  const buildExpense = (expenseData, expenseId = null) => {
    const amt = parseFloat(String(expenseData.amount).replace(/,/g, ''));
    const selected = expenseData.splitBetween && expenseData.splitBetween.length > 0 ? expenseData.splitBetween : state.people || [];
    const who = selected;
    
    let customAmounts;
    if (expenseData.customTaxMode) {
      // Calculate totals for custom tax mode
      const personTotals = {};
      const personSubtotals = {};
      const personTaxShares = {};
      const taxAmount = round2(parseFloat(String(expenseData.taxAmount || 0).replace(/,/g, '')) || 0);
      const tipAmount = round2(parseFloat(String(expenseData.tipAmount || 0).replace(/,/g, '')) || 0);
      const totalTaxTip = round2(taxAmount + tipAmount);
      const items = expenseData.items || [];
      
      // Initialize person totals
      selected.forEach((p) => {
        personSubtotals[p] = 0;
        personTaxShares[p] = 0;
      });
      
      // Calculate total subtotal
      let totalSubtotal = 0;
      items.forEach((item) => {
        const itemAmount = round2(Number(item.amount) || 0);
        totalSubtotal += itemAmount;
        
        // Distribute item amount to people who share it
        const itemPeople = item.splitBetween || [];
        if (itemPeople.length > 0) {
          const sharePerPerson = round2(itemAmount / itemPeople.length);
          itemPeople.forEach((p) => {
            if (selected.includes(p)) {
              personSubtotals[p] = round2((personSubtotals[p] || 0) + sharePerPerson);
            }
          });
        }
      });
      
      // Calculate tax+tip per item and distribute to people
      items.forEach((item) => {
        const itemAmount = round2(Number(item.amount) || 0);
        if (totalSubtotal > 0 && itemAmount > 0) {
          const itemTaxTip = round2((itemAmount / totalSubtotal) * totalTaxTip);
          const itemPeople = item.splitBetween || [];
          if (itemPeople.length > 0) {
            const taxTipPerPerson = round2(itemTaxTip / itemPeople.length);
            itemPeople.forEach((p) => {
              if (selected.includes(p)) {
                personTaxShares[p] = round2((personTaxShares[p] || 0) + taxTipPerPerson);
              }
            });
          }
        }
      });
      
      // Calculate final totals
      selected.forEach((p) => {
        personTotals[p] = round2((personSubtotals[p] || 0) + (personTaxShares[p] || 0));
      });
      customAmounts = personTotals;
    } else {
      const evenShare = who.length > 0 ? amt / who.length : 0;
      const evenAmounts = Object.fromEntries(who.map((p) => [p, evenShare]));
      customAmounts = expenseData.autoSplit ? undefined : { ...evenAmounts, ...(expenseData.customAmounts || {}) };
    }

    return {
      id: expenseId || generateId(),
      name: expenseData.name.trim(),
      emoji: expenseData.emoji || 'receipt_long',
      date: expenseData.date || '',
      amount: amt,
      currency: (expenseData.currency || state.currency || DEFAULT_CURRENCY).trim() || state.currency || DEFAULT_CURRENCY,
      splitType: expenseData.customTaxMode ? 'customTax' : (expenseData.autoSplit ? 'even' : 'custom'),
      splitBetween: expenseData.autoSplit && !expenseData.customTaxMode ? who : null,
      customAmounts: customAmounts,
      items: expenseData.customTaxMode ? expenseData.items : undefined,
      taxAmount: expenseData.customTaxMode ? expenseData.taxAmount : undefined,
      tipAmount: expenseData.customTaxMode ? expenseData.tipAmount : undefined,
      paidBy: expenseData.paidBy || (who.length > 0 ? who[0] : '') || '',
      notes: expenseData.notes || '',
    };
  };

  const validateExpense = (expenseData) => {
    const amt = parseFloat(String(expenseData.amount).replace(/,/g, ''));
    if (!expenseData.name.trim() || isNaN(amt) || amt <= 0) return false;

    const selected = expenseData.splitBetween && expenseData.splitBetween.length > 0 ? expenseData.splitBetween : state.people || [];
    if (selected.length === 0) return false;

    if (expenseData.customTaxMode) {
      // Validate custom tax mode
      const personTotals = {};
      const personSubtotals = {};
      const personTaxShares = {};
      const taxAmount = round2(parseFloat(String(expenseData.taxAmount || 0).replace(/,/g, '')) || 0);
      const tipAmount = round2(parseFloat(String(expenseData.tipAmount || 0).replace(/,/g, '')) || 0);
      const totalTaxTip = round2(taxAmount + tipAmount);
      const items = expenseData.items || [];
      
      // Initialize person totals
      selected.forEach((p) => {
        personSubtotals[p] = 0;
        personTaxShares[p] = 0;
      });
      
      // Calculate total subtotal
      let totalSubtotal = 0;
      items.forEach((item) => {
        const itemAmount = round2(Number(item.amount) || 0);
        totalSubtotal += itemAmount;
        
        // Distribute item amount to people who share it
        const itemPeople = item.splitBetween || [];
        if (itemPeople.length > 0) {
          const sharePerPerson = round2(itemAmount / itemPeople.length);
          itemPeople.forEach((p) => {
            if (selected.includes(p)) {
              personSubtotals[p] = round2((personSubtotals[p] || 0) + sharePerPerson);
            }
          });
        }
      });
      
      // Calculate tax+tip per item and distribute to people
      items.forEach((item) => {
        const itemAmount = round2(Number(item.amount) || 0);
        if (totalSubtotal > 0 && itemAmount > 0) {
          const itemTaxTip = round2((itemAmount / totalSubtotal) * totalTaxTip);
          const itemPeople = item.splitBetween || [];
          if (itemPeople.length > 0) {
            const taxTipPerPerson = round2(itemTaxTip / itemPeople.length);
            itemPeople.forEach((p) => {
              if (selected.includes(p)) {
                personTaxShares[p] = round2((personTaxShares[p] || 0) + taxTipPerPerson);
              }
            });
          }
        }
      });
      
      // Calculate final totals
      selected.forEach((p) => {
        personTotals[p] = round2((personSubtotals[p] || 0) + (personTaxShares[p] || 0));
      });
      const sum = Object.values(personTotals).reduce((a, b) => a + b, 0);
      if (Math.abs(sum - amt) > 0.02) return false;
    } else if (!expenseData.autoSplit) {
      const sum = Object.values(expenseData.customAmounts || {}).reduce((a, b) => a + (Number(b) || 0), 0);
      if (Math.abs(sum - amt) > 0.02) return false;
    } else if (!state.people || state.people.length === 0) {
      return false;
    }

    return true;
  };

  const addExpense = () => {
    if (!validateExpense(newExpense)) return;
    const expense = buildExpense(newExpense);
    save((s) => ({ ...s, expenses: [...(s.expenses || []), expense] }));
    setNewExpense(createEmptyExpense());
    setAddExpenseOpen(false);
  };

  const startEditExpense = (expense) => {
    setAddExpenseOpen(false);
    const customKeys = expense.customAmounts ? Object.keys(expense.customAmounts) : [];
    const isCustomTax = expense.splitType === 'customTax';
    setEditingExpenseId(expense.id);
    setEditExpense({
      name: expense.name || '',
      emoji: expense.emoji || 'receipt_long',
      date: expense.date || '',
      amount: expense.amount != null ? String(expense.amount) : '',
      currency: expense.currency || state.currency || DEFAULT_CURRENCY,
      splitBetween: isCustomTax ? customKeys : (expense.splitType === 'custom' ? customKeys : (Array.isArray(expense.splitBetween) ? expense.splitBetween : [])),
      customAmounts: expense.customAmounts ? { ...expense.customAmounts } : {},
      autoSplit: !isCustomTax && expense.splitType !== 'custom',
      paidBy: expense.paidBy || '',
      customTaxMode: isCustomTax,
      items: expense.items ? [...expense.items] : [],
      taxAmount: expense.taxAmount ?? 0,
      tipAmount: expense.tipAmount ?? 0,
      notes: expense.notes || '',
    });
  };

  const saveEditedExpense = () => {
    if (!validateExpense(editExpense)) return;
    const updated = buildExpense(editExpense, editingExpenseId);
    save((s) => ({
      ...s,
      expenses: (s.expenses || []).map((e) => (e.id === editingExpenseId ? updated : e)),
    }));
    setEditingExpenseId(null);
  };

  const cancelEditExpense = () => {
    setEditingExpenseId(null);
  };

  const deleteExpense = (id) => {
    save((s) => ({ ...s, expenses: (s.expenses || []).filter((e) => e.id !== id) }));
    if (editingExpenseId === id) setEditingExpenseId(null);
  };

  const isPersonInUse = (name) =>
    (state.expenses || []).some(
      (e) =>
        (e.splitBetween && e.splitBetween.includes(name)) ||
        (e.customAmounts && Object.keys(e.customAmounts || {}).includes(name))
    );

  // Computed values
  const transactions = computeSettleUp(state.people || [], state.expenses || [], state.currency || DEFAULT_CURRENCY, state.ratesToTrip, state.groups || []);
  const tripCurrency = state.currency || DEFAULT_CURRENCY;
  const ratesToTrip = state.ratesToTrip || {};
  const totalSpent = round2(
    (state.expenses || []).reduce((sum, exp) => {
      const expCurrency = exp.currency || tripCurrency;
      return sum + toTrip(Number(exp.amount) || 0, expCurrency, tripCurrency, ratesToTrip);
    }, 0)
  );
  const peopleSpendList = calculatePeopleSpend(state.people || [], state.expenses || [], tripCurrency, ratesToTrip);

  const headingStyle = { fontFamily: FONT_FAMILY };

  const handleUnlock = (e) => {
    e.preventDefault();
    const trimmed = codeInput.trim();
    if (pendingGuestData) {
      const tripCode = pendingGuestData.shareCode;
      const pendingMerged = pendingGuestData.merged;
      (async () => {
        let merged = null;
        let tripName = 'Shared Trip';
        let storedPasscode = '';
        if (BACKEND_ENABLED && tripCode) {
          const fromBackend = await fetchTripFromBackend(tripCode);
          if (fromBackend?.data) {
            const built = buildTripData(fromBackend.data);
            merged = built;
            tripName = fromBackend.name || tripName;
            storedPasscode = built.passcode || '';
          }
        }
        if (!merged && pendingMerged) {
          merged = pendingMerged;
          storedPasscode = merged.passcode || storedPasscode;
        }
        if (!merged && tripCode) {
          try {
            const saved = localStorage.getItem(GUEST_STORAGE_PREFIX + tripCode);
            if (saved) {
              const parsed = JSON.parse(saved);
              const built = buildTripData(parsed.tripData || parsed);
              merged = built;
              tripName = parsed.tripName || tripName;
              storedPasscode = built.passcode || storedPasscode;
            }
          } catch (_) {}
        }
        if (!merged) {
          setCodeError(true);
          return;
        }
        if (!storedPasscode || trimmed !== storedPasscode) {
          setCodeError(true);
          return;
        }
        sessionStorage.setItem(UNLOCK_KEY, 'guest:' + tripCode);
        setUnlockRole({ guest: tripCode });
        setCodeError(false);
        setCodeInput('');
        setPendingGuestData(null);
        const sharedTripId = generateId();
        setTrips([{ id: sharedTripId, name: tripName, data: merged, shareCode: tripCode }]);
        setActiveTripId(sharedTripId);
        setState(merged);
      })();
      return;
    }
    if (trimmed === ADMIN_CODE) {
      sessionStorage.setItem(UNLOCK_KEY, 'admin');
      setUnlockRole('admin');
      setCodeError(false);
      setCodeInput('');
    } else {
      setCodeError(true);
    }
  };

  const handleLock = () => {
    sessionStorage.removeItem(UNLOCK_KEY);
    setUnlockRole(null);
    setPendingGuestData(null);
    setCodeInput('');
    setCodeError(false);
    setTrips([]);
    setActiveTripId(null);
    setState(defaultState());
  };

  if (!unlocked) {
    const isGuestLink = !!pendingGuestData;
    return (
      <div
        className="min-h-screen font-sans flex items-center justify-center p-4"
        style={{ background: colors.bg, color: colors.text, fontFamily: FONT_FAMILY }}
      >
        <div
          className="w-full max-w-sm rounded-2xl border p-8 shadow-lg"
          style={{ borderColor: colors.border, background: colors.card, boxShadow: shadows.card }}
        >
          <h1 className="text-xl font-bold mb-2" style={{ ...headingStyle, color: colors.primary }}>
            Trip Split
          </h1>
          <p className="text-sm mb-6" style={{ color: colors.muted }}>
            {isGuestLink ? 'Enter the code for this trip (from the trip owner)' : 'Enter admin code to manage all trips'}
          </p>
          <form onSubmit={handleUnlock}>
            <input
              type="password"
              autoComplete="off"
              value={codeInput}
              onChange={(e) => { setCodeInput(e.target.value); setCodeError(false); }}
              placeholder={isGuestLink ? 'Trip code' : 'Admin code'}
              className="w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 mb-3"
              style={{ borderColor: codeError ? colors.accent : colors.border, background: colors.bg, color: colors.text }}
            />
            {codeError && (
              <p className="text-sm mb-3" style={{ color: colors.accent }}>
                {isGuestLink ? 'Wrong trip code. Ask the trip owner for the code.' : 'Wrong code. If you\'re a guest, open the shareable link from the trip owner first, then enter the code they gave you.'}
              </p>
            )}
            <Button type="submit" className="w-full">
              Unlock
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen font-sans"
      style={{
        background: colors.bg,
        color: colors.text,
        fontFamily: FONT_FAMILY,
      }}
    >
      <header
        className="sticky top-0 z-10"
        style={{
          background: 'rgba(255,255,255,0.8)',
          borderBottom: `1px solid ${colors.border}`,
          boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
          backdropFilter: 'blur(12px)',
          fontFamily: FONT_FAMILY,
        }}
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4 md:px-8">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold" style={{ ...headingStyle, color: colors.primary }}>
              Trip Split
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && activeTrip?.shareCode && (
              <span className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: colors.border, color: colors.muted, background: colors.bg }}>
                Trip code: <strong style={{ color: colors.text }}>{activeTrip.shareCode}</strong>
              </span>
            )}
            <Button variant="secondary" onClick={handleLock} className="text-xs py-1.5">
              Lock
            </Button>
            <Button onClick={copyShareableLink}>
              {copied ? 'Link copied!' : 'Copy shareable link'}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row">
          <TripSidebar
            trips={trips}
            activeTripId={activeTripId}
            onSelectTrip={selectTrip}
            onCreateTrip={createTrip}
            isAdmin={isAdmin}
            colors={colors}
            headingStyle={headingStyle}
          />

          <div className="flex-1">
            {settingsOpen ? (
              <TripSettingsPage
                activeTrip={activeTrip}
                isEditingTripName={isEditingTripName}
                tripNameDraft={tripNameDraft}
                setTripNameDraft={setTripNameDraft}
                setIsEditingTripName={setIsEditingTripName}
                saveTripName={saveTripName}
                state={state}
                save={save}
                setTripDates={setTripDates}
                fetchRates={fetchRates}
                fetchRatesLoading={fetchRatesLoading}
                fetchRatesError={fetchRatesError}
                addPerson={addPerson}
                removePerson={removePerson}
                createGroup={createGroup}
                removeGroup={removeGroup}
                isPersonInUse={isPersonInUse}
                deleteTrip={deleteTrip}
                onClose={() => setSettingsOpen(false)}
                colors={colors}
                headingStyle={headingStyle}
              />
            ) : (
              <TripMainView
                activeTrip={activeTrip}
                state={state}
                isAdmin={isAdmin}
                onOpenSettings={() => {
                  if (isAdmin) setSettingsOpen(true);
                }}
                totalSpent={totalSpent}
                tripCurrency={tripCurrency}
                peopleSpendList={peopleSpendList}
                editingExpenseId={editingExpenseId}
                editExpense={editExpense}
                setEditExpense={setEditExpense}
                onSaveEditedExpense={saveEditedExpense}
                onCancelEditExpense={cancelEditExpense}
                onStartEditExpense={startEditExpense}
                addExpenseOpen={addExpenseOpen}
                newExpense={newExpense}
                setNewExpense={setNewExpense}
                onAddExpense={addExpense}
                onCancelAddExpense={() => {
                  setAddExpenseOpen(false);
                  setNewExpense(createEmptyExpense());
                }}
                setAddExpenseOpen={setAddExpenseOpen}
                expandedExpenses={expandedExpenses}
                setExpandedExpenses={setExpandedExpenses}
                onDeleteExpense={deleteExpense}
                transactions={transactions}
                colors={colors}
                headingStyle={headingStyle}
                ratesToTrip={ratesToTrip}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Sub-components
function TripSidebar({ trips, activeTripId, onSelectTrip, onCreateTrip, isAdmin, colors, headingStyle }) {
  return (
    <aside className="w-full rounded-2xl border p-4 lg:w-72" style={{ borderColor: colors.border, background: colors.card, boxShadow: shadows.card }}>
      {isAdmin && (
        <Button onClick={onCreateTrip} className="w-full">Create trip</Button>
      )}
      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide" style={{ ...headingStyle, color: colors.muted }}>
        {isAdmin ? 'Trips' : 'Trip'}
      </h2>
      <div className="mt-3 flex flex-col gap-2">
        {trips.map((trip) => {
          const isActive = trip.id === activeTripId;
          return (
            <button
              key={trip.id}
              type="button"
              onClick={() => onSelectTrip(trip.id)}
              className="w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition"
              style={{
                background: isActive ? colors.primary : colors.bg,
                color: isActive ? '#fff' : colors.text,
                border: `1px solid ${colors.border}`,
              }}
            >
              {trip.name}
            </button>
          );
        })}
        {trips.length === 0 && (
          <p className="text-xs" style={{ color: colors.muted }}>
            {isAdmin ? 'No trips yet.' : 'No trip loaded.'}
          </p>
        )}
      </div>
    </aside>
  );
}

function TripMainView({
  activeTrip,
  state,
  isAdmin,
  onOpenSettings,
  totalSpent,
  tripCurrency,
  peopleSpendList,
  editingExpenseId,
  editExpense,
  setEditExpense,
  onSaveEditedExpense,
  onCancelEditExpense,
  onStartEditExpense,
  addExpenseOpen,
  newExpense,
  setNewExpense,
  onAddExpense,
  onCancelAddExpense,
  setAddExpenseOpen,
  expandedExpenses,
  setExpandedExpenses,
  onDeleteExpense,
  transactions,
  colors,
  headingStyle,
  ratesToTrip,
}) {
  return (
    <>
      <TripInfoCard
        activeTrip={activeTrip}
        state={state}
        isAdmin={isAdmin}
        onOpenSettings={onOpenSettings}
        totalSpent={totalSpent}
        tripCurrency={tripCurrency}
        peopleSpendList={peopleSpendList}
        colors={colors}
        headingStyle={headingStyle}
      />

      <ExpensesSection
        state={state}
        editingExpenseId={editingExpenseId}
        editExpense={editExpense}
        setEditExpense={setEditExpense}
        onSaveEditedExpense={onSaveEditedExpense}
        onCancelEditExpense={onCancelEditExpense}
        onStartEditExpense={onStartEditExpense}
        addExpenseOpen={addExpenseOpen}
        newExpense={newExpense}
        setNewExpense={setNewExpense}
                onAddExpense={onAddExpense}
                onCancelAddExpense={onCancelAddExpense}
                onOpenAddExpense={() => {
                  setNewExpense((prev) => ({ ...prev, currency: state.currency || DEFAULT_CURRENCY, paidBy: (state.people || [])[0] || '' }));
                  setAddExpenseOpen(true);
                }}
                expandedExpenses={expandedExpenses}
                setExpandedExpenses={setExpandedExpenses}
                onDeleteExpense={onDeleteExpense}
                tripCurrency={tripCurrency}
                ratesToTrip={ratesToTrip}
                colors={colors}
                headingStyle={headingStyle}
              />

      <SettleUpSection transactions={transactions} state={state} colors={colors} headingStyle={headingStyle} />
    </>
  );
}

function TripInfoCard({ activeTrip, state, isAdmin, onOpenSettings, totalSpent, tripCurrency, peopleSpendList, colors, headingStyle }) {
  return (
    <Card className="mb-6 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-semibold" style={{ ...headingStyle, color: colors.text }}>
            {activeTrip?.name || 'Trip'}
          </h2>
          {(state.tripStartDate || state.tripEndDate) && (
            <span className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: colors.border, color: colors.muted, background: colors.bg }}>
              {formatDateRange(state.tripStartDate, state.tripEndDate)}
            </span>
          )}
        </div>
        {isAdmin && (
          <Button variant="secondary" onClick={onOpenSettings} className="inline-flex items-center gap-2">
            <span className="material-symbols-outlined text-base">settings</span>
            Settings
          </Button>
        )}
      </div>
      <div className="mt-3 space-y-3 text-xs sm:text-sm">
        <div style={{ color: colors.muted }}>
          Total spent: <span className="font-semibold" style={{ color: colors.text }}>{totalSpent.toFixed(2)} {tripCurrency}</span>
        </div>
        <div>
          <div className="mb-2" style={{ color: colors.muted }}>
            Total spent per person:
          </div>
          <div className="grid grid-cols-2 gap-2">
            {peopleSpendList.map(({ name, amount }) => (
              <div key={name} className="rounded-lg border px-3 py-2" style={{ borderColor: colors.border, background: colors.bg }}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold" style={{ color: colors.text }}>{name}</span>
                  <span style={{ color: colors.muted }}>{amount.toFixed(2)} {tripCurrency}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ExpensesSection({
  state,
  editingExpenseId,
  editExpense,
  setEditExpense,
  onSaveEditedExpense,
  onCancelEditExpense,
  onStartEditExpense,
  addExpenseOpen,
  newExpense,
  setNewExpense,
  onAddExpense,
  onCancelAddExpense,
  onOpenAddExpense,
  expandedExpenses,
  setExpandedExpenses,
  onDeleteExpense,
  tripCurrency,
  ratesToTrip,
  colors,
  headingStyle,
}) {
  const [sortBy, setSortBy] = useState('date');

  const sortedExpenses = useMemo(() => {
    const expenses = [...(state.expenses || [])];
    switch (sortBy) {
      case 'date':
        return expenses.sort((a, b) => {
          const aTime = a.date ? Date.parse(a.date) : 0;
          const bTime = b.date ? Date.parse(b.date) : 0;
          return bTime - aTime; // newest first
        });
      case 'amount':
        return expenses.sort((a, b) => {
          const aAmt = Number(a.amount) || 0;
          const bAmt = Number(b.amount) || 0;
          return bAmt - aAmt; // highest first
        });
      case 'alphabetical':
        return expenses.sort((a, b) => {
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          return aName.localeCompare(bName);
        });
      default:
        return expenses;
    }
  }, [state.expenses, sortBy]);

  return (
    <>
      <section className="mb-4">
        <h2 className="mb-3 text-lg font-semibold" style={{ ...headingStyle, color: colors.primary }}>
          Expenses
        </h2>

        {editingExpenseId ? (
          <ExpenseForm
            people={state.people || []}
            groups={state.groups || []}
            value={editExpense}
            onChange={setEditExpense}
            onSave={onSaveEditedExpense}
            onCancel={onCancelEditExpense}
            tripCurrency={tripCurrency}
            ratesToTrip={ratesToTrip}
            colors={colors}
            submitLabel="Save changes"
          />
        ) : !addExpenseOpen ? (
          <>
            <Button onClick={onOpenAddExpense}>+ Add expense</Button>
            {(state.expenses || []).length > 0 && (
              <div className="mt-3 flex justify-end items-center gap-3">
                <span className="text-sm font-bold" style={{ color: colors.muted }}>Sort by:</span>
                <button
                  type="button"
                  onClick={() => setSortBy('date')}
                  className={`flex items-center gap-1 text-sm font-bold ${sortBy === 'date' ? '' : 'opacity-50 hover:opacity-75'}`}
                  style={{ color: sortBy === 'date' ? colors.primary : colors.muted }}
                >
                  <span className="material-symbols-outlined text-base">calendar_today</span>
                  date
                </button>
                <button
                  type="button"
                  onClick={() => setSortBy('alphabetical')}
                  className={`flex items-center gap-1 text-sm font-bold ${sortBy === 'alphabetical' ? '' : 'opacity-50 hover:opacity-75'}`}
                  style={{ color: sortBy === 'alphabetical' ? colors.primary : colors.muted }}
                >
                  <span className="material-symbols-outlined text-base">sort_by_alpha</span>
                  alphabetically
                </button>
                <button
                  type="button"
                  onClick={() => setSortBy('amount')}
                  className={`flex items-center gap-1 text-sm font-bold ${sortBy === 'amount' ? '' : 'opacity-50 hover:opacity-75'}`}
                  style={{ color: sortBy === 'amount' ? colors.primary : colors.muted }}
                >
                  <span className="material-symbols-outlined text-base">attach_money</span>
                  expense
                </button>
              </div>
            )}
          </>
        ) : (
          <ExpenseForm
            people={state.people || []}
            groups={state.groups || []}
            value={newExpense}
            onChange={setNewExpense}
            onSave={onAddExpense}
            onCancel={onCancelAddExpense}
            tripCurrency={tripCurrency}
            ratesToTrip={ratesToTrip}
            colors={colors}
            submitLabel="Add expense"
          />
        )}
      </section>

      {(state.expenses || []).length > 0 && (
        <ul className="mb-10 space-y-3">
          {sortedExpenses.map((expense) => (
              <ExpenseItem
                key={expense.id}
                expense={expense}
                isExpanded={expandedExpenses.includes(expense.id)}
                onToggleExpand={() => {
                  if (expandedExpenses.includes(expense.id)) {
                    setExpandedExpenses((prev) => prev.filter((id) => id !== expense.id));
                  } else {
                    setExpandedExpenses((prev) => [...prev, expense.id]);
                  }
                }}
                onEdit={() => onStartEditExpense(expense)}
                onDelete={() => onDeleteExpense(expense.id)}
                tripCurrency={tripCurrency}
                ratesToTrip={ratesToTrip}
                colors={colors}
              />
            ))}
        </ul>
      )}
    </>
  );
}

function ExpenseItem({ expense, isExpanded, onToggleExpand, onEdit, onDelete, tripCurrency, ratesToTrip, colors }) {
  const expCurrency = expense.currency || tripCurrency;
  const amtInTrip = toTrip(Number(expense.amount), expCurrency, tripCurrency, ratesToTrip);

  return (
    <li className="rounded-2xl border overflow-hidden" style={{ borderColor: colors.border, background: colors.card, boxShadow: shadows.expense }}>
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:opacity-80 transition"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="material-symbols-outlined text-lg flex-shrink-0" aria-hidden="true">
            {expense.emoji || 'receipt_long'}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate" style={{ color: colors.text }}>{expense.name}</div>
            {(expense.date || expense.paidBy) && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {expense.date && (
                  <span className="rounded-full border px-3 py-1 text-xs flex-shrink-0" style={{ borderColor: colors.border, color: colors.muted, background: colors.bg }}>
                    {formatDateISO(expense.date)}
                  </span>
                )}
                {expense.paidBy && (
                  <span className="text-xs flex-shrink-0" style={{ color: colors.muted }}>
                    Paid by <span className="font-semibold">{expense.paidBy}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-lg font-bold" style={{ color: colors.text }}>
            {expCurrency} {Number(expense.amount).toFixed(2)}
          </span>
          <span className={`material-symbols-outlined transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: colors.muted }}>
            expand_more
          </span>
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: colors.border }}>
          {expCurrency !== tripCurrency && (
            <div className="mt-3 text-sm" style={{ color: colors.muted }}>
              Total: {tripCurrency} {amtInTrip.toFixed(2)}
            </div>
          )}
          <div className="mt-3 text-sm" style={{ color: colors.muted }}>
            {expense.splitType === 'customTax'
              ? (() => {
                  const items = expense.items || [];
                  const taxAmt = round2(parseFloat(String(expense.taxAmount || 0).replace(/,/g, '')) || 0);
                  const tipAmt = round2(parseFloat(String(expense.tipAmount || 0).replace(/,/g, '')) || 0);
                  const totalTaxTip = round2(taxAmt + tipAmt);
                  const totalSubtotal = items.reduce((sum, item) => sum + round2(Number(item.amount) || 0), 0);
                  
                  // Build summary by person
                  const personSummary = {};
                  Object.keys(expense.customAmounts || {}).forEach((p) => {
                    personSummary[p] = { items: [], tax: 0, subtotal: 0 };
                  });
                  
                  items.forEach((item) => {
                    const itemAmount = round2(Number(item.amount) || 0);
                    const itemTaxTip = totalSubtotal > 0 ? round2((itemAmount / totalSubtotal) * totalTaxTip) : 0;
                    const itemPeople = item.splitBetween && item.splitBetween.length > 0 ? item.splitBetween : Object.keys(personSummary);
                    const amountPerPerson = itemPeople.length > 0 ? round2(itemAmount / itemPeople.length) : 0;
                    const taxTipPerPerson = itemPeople.length > 0 ? round2(itemTaxTip / itemPeople.length) : 0;
                    
                    itemPeople.forEach((p) => {
                      if (personSummary[p]) {
                        personSummary[p].items.push(`${item.name || 'Item'}: ${expCurrency} ${amountPerPerson.toFixed(2)}`);
                        personSummary[p].subtotal += amountPerPerson;
                        personSummary[p].tax += taxTipPerPerson;
                      }
                    });
                  });
                  
                  return Object.entries(expense.customAmounts || {}).map(([p, total]) => {
                    const summary = personSummary[p] || { items: [], tax: 0, subtotal: 0 };
                    const itemsList = summary.items.join(', ');
                    return `${p}: ${itemsList}${totalTaxTip > 0 ? ` + tax/tip (${expCurrency} ${round2(summary.tax).toFixed(2)})` : ''} = ${expCurrency} ${Number(total).toFixed(2)}`;
                  }).join('; ');
                })()
              : expense.splitType === 'custom'
              ? `Split: ${Object.entries(expense.customAmounts || {}).map(([p, a]) => `${p} ${expCurrency} ${Number(a).toFixed(2)}`).join(', ')}`
              : expense.splitBetween && expense.splitBetween.length > 0
              ? `Split between: ${expense.splitBetween.join(', ')}`
              : 'Split evenly among everyone'}
          </div>
          {expense.notes && (
            <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
              <p className="text-sm font-medium mb-1" style={{ color: colors.text }}>Notes</p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: colors.muted }}>{expense.notes}</p>
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button variant="secondary" onClick={onEdit} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm">
              <span className="material-symbols-outlined text-base">edit</span>
              Edit
            </Button>
            <Button variant="secondary" onClick={onDelete} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm" style={{ color: colors.accent }}>
              <span className="material-symbols-outlined text-base">delete</span>
              Delete
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function SettleUpSection({ transactions, state, colors, headingStyle }) {
  return (
    <section className="mb-10">
      <h2 className="mb-3 text-lg font-semibold" style={{ ...headingStyle, color: colors.primary }}>
        Who owes whom
      </h2>
      <Card className="p-4" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
        {transactions.length === 0 ? (
          <p className="text-sm" style={{ color: colors.muted }}>
            {state.people && state.people.length > 0 && (state.expenses || []).length > 0
              ? 'All even.'
              : 'Add people and expenses to see who owes whom.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {transactions.map((t, i) => {
              const trip = state.currency || DEFAULT_CURRENCY;
              const r = state.ratesToTrip || {};
              const inUSD = trip === 'USD' ? t.amount : t.amount / (r['USD'] || 1);
              const inCAD = trip === 'CAD' ? t.amount : t.amount / (r['CAD'] || 1);
              const showUsd = state.showUsd !== false;
              return (
                <li key={i} className="text-sm">
                  <span className="font-medium">{t.from}</span>
                  <span style={{ color: colors.muted }}> owes </span>
                  <span className="font-medium">{t.to}</span>
                  <span style={{ color: colors.muted }}>
                    {' '}
                    {showUsd
                      ? `${round2(inUSD).toFixed(2)} USD / ${round2(inCAD).toFixed(2)} CAD`
                      : `${round2(t.amount).toFixed(2)} ${trip}`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </section>
  );
}

function TripSettingsPage({
  activeTrip,
  isEditingTripName,
  tripNameDraft,
  setTripNameDraft,
  setIsEditingTripName,
  saveTripName,
  state,
  save,
  setTripDates,
  fetchRates,
  fetchRatesLoading,
  fetchRatesError,
  addPerson,
  removePerson,
  createGroup,
  removeGroup,
  isPersonInUse,
  deleteTrip,
  onClose,
  colors,
  headingStyle,
}) {
  return (
    <section className="mb-10">
      <Card className="p-5">
        <Button variant="text" onClick={onClose} className="mb-4 inline-flex items-center gap-1">
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to trip
        </Button>
        <h2 className="mb-4 text-lg font-semibold" style={{ ...headingStyle, color: '#0D0D0D' }}>
          Trip settings
        </h2>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {isEditingTripName ? (
              <Input
                value={tripNameDraft}
                onChange={(e) => setTripNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveTripName();
                  if (e.key === 'Escape') {
                    setTripNameDraft(activeTrip?.name || '');
                    setIsEditingTripName(false);
                  }
                }}
                className="text-base font-semibold"
              />
            ) : (
              <h3 className="text-base font-semibold" style={{ ...headingStyle, color: colors.text }}>
                {activeTrip?.name || 'Trip'}
              </h3>
            )}
            <Button variant="secondary" onClick={() => {
              if (isEditingTripName) {
                saveTripName();
              } else {
                setTripNameDraft(activeTrip?.name || '');
                setIsEditingTripName(true);
              }
            }}>
              {isEditingTripName ? 'Save title' : 'Edit title'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={state.tripStartDate || ''}
              onChange={(e) => setTripDates({ tripStartDate: e.target.value })}
              className="h-9"
              style={{ accentColor: colors.primary, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)' }}
            />
            <span className="text-sm" style={{ color: colors.muted }}>→</span>
            <Input
              type="date"
              value={state.tripEndDate || ''}
              onChange={(e) => setTripDates({ tripEndDate: e.target.value })}
              className="h-9"
              style={{ accentColor: colors.primary, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)' }}
            />
          </div>
        </div>

        <div className="mt-5 border-t pt-5" style={{ borderColor: colors.border }}>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide" style={{ ...headingStyle, color: colors.muted }}>
            Currency &amp; conversion
          </h3>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium" style={{ color: colors.muted }}>
              Currency
            </label>
            <select
              value={state.currency || DEFAULT_CURRENCY}
              onChange={(e) => {
                const trip = e.target.value;
                save((s) => ({ ...s, currency: trip, ratesToTrip: { [trip]: 1 } }));
              }}
              className="w-full max-w-[140px] rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: colors.border, background: colors.bg, color: colors.text }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <p className="mt-1 text-xs" style={{ color: colors.muted }}>
              Default for new expenses. You can set a different currency per expense.
            </p>
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={state.showUsd !== false}
                onChange={(e) => save((s) => ({ ...s, showUsd: e.target.checked }))}
                className="rounded border w-4 h-4"
                style={{ accentColor: colors.primary, borderColor: colors.border }}
              />
              <span className="text-sm font-medium" style={{ color: colors.text }}>
                Show USD in &quot;Who owes whom&quot;
              </span>
            </label>
            <p className="mt-1 text-xs ml-6" style={{ color: colors.muted }}>
              When off, only the trip currency is shown (no USD).
            </p>
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium" style={{ color: colors.muted }}>
              Trip passcode (guests type this to unlock)
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="text"
                value={state.passcode || ''}
                onChange={(e) => save((s) => ({ ...s, passcode: e.target.value }))}
                placeholder="e.g. paris2026"
                className="max-w-xs"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => save((s) => ({ ...s, passcode: generatePasscode() }))}
                className="text-xs"
              >
                Regenerate
              </Button>
            </div>
            <p className="mt-1 text-xs" style={{ color: colors.muted }}>
              Different from the trip code in the link. Share this passcode with friends so they can unlock the trip.
            </p>
          </div>

          <div className="border-t pt-4 mt-4" style={{ borderColor: colors.border }}>
            <p className="mb-3 text-sm" style={{ color: colors.muted }}>
              Amounts in other currencies are converted to {state.currency || DEFAULT_CURRENCY} using these rates (1 [currency] = ? [trip]). Fetched rates are from an exchange-rate API; you can edit any value to use your own rate.
            </p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Button onClick={fetchRates} disabled={fetchRatesLoading} className="px-3 py-1.5">
                {fetchRatesLoading ? 'Fetching…' : 'Fetch from internet'}
              </Button>
              {fetchRatesError && (
                <span className="text-sm" style={{ color: colors.accent }}>
                  {fetchRatesError}
                </span>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {(() => {
                const trip = state.currency || DEFAULT_CURRENCY;
                const used = new Set((state.expenses || []).map((e) => (e.currency || trip).trim()).filter(Boolean));
                const show = ['USD', 'CAD', 'EUR', 'GBP'].filter((c) => c !== trip && (c === 'USD' || c === 'CAD' || used.has(c)));
                return show.map((c) => (
                  <div key={c} className="flex items-center gap-2 text-sm">
                    <span className="w-12">{c}</span>
                    <span style={{ color: colors.muted }}>1 {c} =</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={(state.ratesToTrip || {})[c] != null ? String(state.ratesToTrip[c]) : ''}
                      onChange={(e) => {
                        const v = parseFloat(String(e.target.value).replace(/,/g, ''));
                        save((s) => ({
                          ...s,
                          ratesToTrip: { ...(s.ratesToTrip || {}), [c]: (Number.isFinite(v) && v > 0) ? v : 1 },
                        }));
                      }}
                      placeholder="e.g. 1.2"
                      className="w-20 text-right"
                    />
                    <span style={{ color: colors.muted }}>{trip}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>

        <div className="mt-5 border-t pt-5" style={{ borderColor: colors.border }}>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide" style={{ ...headingStyle, color: colors.muted }}>
            People
          </h3>
          <PeopleEditor
            people={state.people || []}
            groups={state.groups || []}
            onAdd={addPerson}
            onRemove={removePerson}
            onCreateGroup={createGroup}
            onRemoveGroup={removeGroup}
            isInUse={isPersonInUse}
            colors={colors}
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={deleteTrip} variant="primary" style={{ background: colors.accent, boxShadow: shadows.buttonAccent }}>
            Delete trip
          </Button>
        </div>
      </Card>
    </section>
  );
}

function GroupTooltip({ groupMembers, colors, children }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  if (!groupMembers || groupMembers.length === 0) {
    return <>{children}</>;
  }

  return (
    <>
      <div className="inline-block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      {showTooltip && (
        <div
          className="fixed z-[1060] pointer-events-none"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div
            className="rounded-lg border px-3 py-2 text-sm shadow-lg whitespace-nowrap"
            style={{
              background: colors.card,
              borderColor: colors.border,
              color: colors.text,
              boxShadow: shadows.tooltip,
            }}
          >
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-base" style={{ color: colors.primary }}>
                group
              </span>
              <span>{groupMembers.join(' + ')}</span>
            </div>
            <div
              className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 border-r border-b"
              style={{
                background: colors.card,
                borderColor: colors.border,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

function PeopleEditor({ people, groups = [], onAdd, onRemove, onCreateGroup, onRemoveGroup, isInUse, colors }) {
  const [name, setName] = useState('');
  const [draggedPerson, setDraggedPerson] = useState(null);
  const [dragOverPerson, setDragOverPerson] = useState(null);

  const getPersonGroup = (personName) => {
    return groups.find((g) => g.people.includes(personName));
  };

  const handleDragStart = (e, personName) => {
    setDraggedPerson(personName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, personName) => {
    e.preventDefault();
    if (draggedPerson && draggedPerson !== personName) {
      setDragOverPerson(personName);
    }
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverPerson(null);
    }
  };

  const handleDrop = (e, targetPerson) => {
    e.preventDefault();
    if (draggedPerson && draggedPerson !== targetPerson) {
      onCreateGroup(draggedPerson, targetPerson);
    }
    setDraggedPerson(null);
    setDragOverPerson(null);
  };

  const handleDragEnd = () => {
    setDraggedPerson(null);
    setDragOverPerson(null);
  };

  const ungroupedPeople = people.filter((p) => !getPersonGroup(p));

  return (
    <Card className="p-4" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
      <div className="mb-3 flex gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onAdd(name);
              setName('');
            }
          }}
          placeholder="Name"
          className="flex-1"
        />
        <Button onClick={() => { onAdd(name); setName(''); }} className="px-4 py-2">
          Add
        </Button>
      </div>

      {groups.length > 0 && (
        <div className="mb-3">
          <div className="mb-2 text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            Groups
          </div>
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => (
              <div
                key={group.id}
                className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm"
                style={{ borderColor: colors.border, background: colors.bg }}
              >
                <GroupTooltip groupMembers={group.people} colors={colors}>
                  <span className="material-symbols-outlined text-base" style={{ color: colors.primary }}>
                    group
                  </span>
                </GroupTooltip>
                <span>{group.people.join(' + ')}</span>
                <button
                  type="button"
                  onClick={() => onRemoveGroup(group.id)}
                  className="ml-1 font-medium hover:underline"
                  style={{ color: colors.accent }}
                  title="Remove group"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {ungroupedPeople.length > 0 && (
        <div className={groups.length > 0 ? 'mt-3' : ''}>
          <div className="mb-2 text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            Individual
          </div>
          <div className="flex flex-wrap gap-2">
            {ungroupedPeople.map((p) => {
              const isDragging = draggedPerson === p;
              const isDragOver = dragOverPerson === p;
              const canDrop = draggedPerson && draggedPerson !== p;
              return (
                <div
                  key={p}
                  draggable
                  onDragStart={(e) => handleDragStart(e, p)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, p)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, p)}
                  onDragEnd={handleDragEnd}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm cursor-move transition-all ${
                    isDragging ? 'opacity-50' : ''
                  } ${isDragOver && canDrop ? 'scale-110 shadow-lg' : ''}`}
                  style={{
                    borderColor: isDragOver && canDrop ? colors.primary : colors.border,
                    background: isDragOver && canDrop ? colors.card : colors.bg,
                    borderWidth: isDragOver && canDrop ? '3px' : '1px',
                    transform: isDragOver && canDrop ? 'scale(1.1)' : undefined,
                  }}
                >
                  {isDragOver && canDrop && (
                    <span className="material-symbols-outlined text-base animate-pulse" style={{ color: colors.primary }}>
                      add_circle
                    </span>
                  )}
                  {p}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(p);
                    }}
                    disabled={isInUse(p)}
                    className="ml-1 font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ color: colors.accent }}
                    title={isInUse(p) ? 'Remove from all expenses first' : 'Remove'}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {people.length > 0 && (
        <p className="mt-2 text-xs" style={{ color: colors.muted }}>
          Drag a name onto another to create a group
        </p>
      )}
    </Card>
  );
}

function ExpenseForm({ people, groups = [], value, onChange, onSave, onCancel, tripCurrency, ratesToTrip, colors, submitLabel }) {
  const expCurrency = value.currency || tripCurrency || DEFAULT_CURRENCY;
  const amt = parseFloat(String(value.amount).replace(/,/g, '')) || 0;
  const selected = value.splitBetween && value.splitBetween.length > 0 ? value.splitBetween : [];
  const splitCount = selected.length > 0 ? selected.length : people.length;
  const customTaxMode = value.customTaxMode || false;
  const items = value.items || []; // All items, each can be shared between people
  const taxAmountNum = round2(parseFloat(String(value.taxAmount ?? '').replace(/,/g, '')) || 0);
  const tipAmountNum = round2(parseFloat(String(value.tipAmount ?? '').replace(/,/g, '')) || 0);
  const totalTaxTip = round2(taxAmountNum + tipAmountNum);
  
  // Calculate totals for custom tax mode
  const personSubtotals = {};
  const personTotals = {};
  const personTaxShares = {};
  if (customTaxMode) {
    // Initialize person totals
    selected.forEach((p) => {
      personSubtotals[p] = 0;
      personTaxShares[p] = 0;
    });
    
    // Calculate total subtotal
    let totalSubtotal = 0;
    items.forEach((item) => {
      const itemAmount = round2(Number(item.amount) || 0);
      totalSubtotal += itemAmount;
      
      // Distribute item amount to people who share it
      const itemPeople = item.splitBetween || [];
      if (itemPeople.length > 0) {
        const sharePerPerson = round2(itemAmount / itemPeople.length);
        itemPeople.forEach((p) => {
          if (selected.includes(p)) {
            personSubtotals[p] = round2((personSubtotals[p] || 0) + sharePerPerson);
          }
        });
      }
    });
    
    // Calculate tax+tip per item and distribute to people
    items.forEach((item) => {
      const itemAmount = round2(Number(item.amount) || 0);
      if (totalSubtotal > 0 && itemAmount > 0) {
        const itemTaxTip = round2((itemAmount / totalSubtotal) * totalTaxTip);
        const itemPeople = item.splitBetween || [];
        const taxTipPerPerson = itemPeople.length > 0 ? round2(itemTaxTip / itemPeople.length) : 0;
        itemPeople.forEach((p) => {
          if (selected.includes(p)) {
            personTaxShares[p] = round2((personTaxShares[p] || 0) + taxTipPerPerson);
          }
        });
      }
    });
    
    // Calculate final totals
    selected.forEach((p) => {
      personTotals[p] = round2((personSubtotals[p] || 0) + (personTaxShares[p] || 0));
    });
  }
  
  const customSum = customTaxMode
    ? Object.values(personTotals).reduce((sum, total) => sum + total, 0)
    : (selected.length > 0 ? selected : people).reduce(
        (sum, p) => sum + (Number((value.customAmounts || {})[p]) || 0),
        0
      );
  const customValid = value.autoSplit || (amt > 0 && Math.abs(customSum - amt) < 0.02);
  const amtInTrip = amt * ((ratesToTrip && ratesToTrip[expCurrency]) ?? (expCurrency === tripCurrency ? 1 : 1));

  const togglePerson = (p) => {
    const next = value.splitBetween.includes(p)
      ? value.splitBetween.filter((x) => x !== p)
      : [...value.splitBetween, p];
    const base = next.length > 0 ? next : people;
    
    if (customTaxMode) {
      // Remove person from all items if they're being removed
      if (value.splitBetween.includes(p) && !next.includes(p)) {
        const updatedItems = items.map((item) => ({
          ...item,
          splitBetween: (item.splitBetween || []).filter((person) => person !== p),
        }));
        onChange({ ...value, splitBetween: next, items: updatedItems });
      } else {
        onChange({ ...value, splitBetween: next });
      }
    } else {
      const count = base.length;
      const even = count > 0 ? round2(amt / count) : 0;
      const nextCustom = Object.fromEntries(base.map((name) => [name, even]));
      onChange({ ...value, splitBetween: next, customAmounts: nextCustom, autoSplit: true });
    }
  };

  const setCustomAmount = (p, v) => {
    const inputStr = String(v).trim();
    let nextVal = 0;
    
    // Check if input is a percentage
    if (inputStr.endsWith('%')) {
      const percentStr = inputStr.slice(0, -1).replace(/,/g, '');
      const percent = parseFloat(percentStr);
      if (!isNaN(percent) && amt > 0) {
        nextVal = round2((percent / 100) * amt);
      }
    } else {
      // Try to evaluate as math expression, fallback to parseFloat
      const evaluated = evaluateMathExpression(inputStr);
      nextVal = evaluated != null ? round2(evaluated) : round2(parseFloat(inputStr.replace(/,/g, '')) || 0);
    }
    
    const base = selected.length > 0 ? selected : people;
    if (!base.includes(p)) return;
    const remaining = round2(amt - nextVal);
    const others = base.filter((name) => name !== p);
    const per = others.length > 0 ? round2(remaining / others.length) : 0;
    const nextCustom = Object.fromEntries(base.map((name) => [name, name === p ? nextVal : per]));
    onChange({ ...value, customAmounts: nextCustom, autoSplit: true });
  };

  const toggleCustomTax = () => {
    if (customTaxMode) {
      // Disable custom tax mode - reset to normal split
      const base = selected.length > 0 ? selected : people;
      const count = base.length;
      const even = count > 0 ? round2(amt / count) : 0;
      const nextCustom = Object.fromEntries(base.map((name) => [name, even]));
      onChange({ ...value, customTaxMode: false, items: [], taxAmount: 0, tipAmount: 0, autoSplit: true, customAmounts: nextCustom });
    } else {
      // Enable custom tax mode - initialize with empty items array
      onChange({ ...value, customTaxMode: true, items: [], taxAmount: 0, tipAmount: 0, autoSplit: false });
    }
  };

  const addItem = () => {
    onChange({
      ...value,
      items: [...items, { id: generateId(), name: '', amount: '', splitBetween: [] }],
    });
  };

  const removeItem = (itemId) => {
    onChange({
      ...value,
      items: items.filter((item) => item.id !== itemId),
    });
  };

  const updateItem = (itemId, field, newValue) => {
    onChange({
      ...value,
      items: items.map((item) =>
        item.id === itemId ? { ...item, [field]: newValue } : item
      ),
    });
  };

  const togglePersonOnItem = (itemId, person) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    const currentSplit = item.splitBetween || [];
    const newSplit = currentSplit.includes(person)
      ? currentSplit.filter((p) => p !== person)
      : [...currentSplit, person];
    updateItem(itemId, 'splitBetween', newSplit);
  };

  const renderPaidBy = (who) => {
    if (!people || people.length === 0) return null;
    const paidByVal = people.includes(value.paidBy) ? value.paidBy : (people[0] || '');
    return (
      <div className="min-w-[180px]">
        <label className="mb-2 block text-sm font-medium" style={{ color: colors.muted }}>
          Paid by
        </label>
        <select
          value={paidByVal}
          onChange={(e) => onChange({ ...value, paidBy: e.target.value })}
          className="w-full rounded-xl border pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ borderColor: colors.border, background: colors.bg, color: colors.text }}
        >
          {people.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <Card className="p-4" style={{ boxShadow: '0 10px 24px rgba(0,0,0,0.06)' }}>
      <div className="mb-3">
        <div className="mb-2 flex flex-wrap items-end gap-2">
          <Input
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            placeholder="Description"
            className="h-10 flex-1"
          />
          <Input
            type="date"
            value={value.date || ''}
            onChange={(e) => onChange({ ...value, date: e.target.value })}
            className="h-10"
            style={{ accentColor: colors.primary, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)' }}
          />
          <select
            value={value.emoji || 'restaurant'}
            onChange={(e) => onChange({ ...value, emoji: e.target.value })}
            className="h-10 rounded-xl border pl-3 pr-10 text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: colors.border, background: colors.bg, color: colors.text }}
          >
            {EXPENSE_CATEGORIES.map((opt) => (
              <option key={opt.id} value={opt.icon}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[120px]">
            <Input
              type="text"
              inputMode="decimal"
              value={value.amount}
              onChange={(e) => {
                const nextAmount = e.target.value;
                if (!value.autoSplit) {
                  onChange({ ...value, amount: nextAmount });
                  return;
                }
                // Try to evaluate as math expression for auto-split calculation
                const evaluated = evaluateMathExpression(nextAmount);
                const nextAmt = evaluated != null ? evaluated : (parseFloat(String(nextAmount).replace(/,/g, '')) || 0);
                const count = splitCount > 0 ? splitCount : 0;
                const even = count > 0 ? round2(nextAmt / count) : 0;
                const base = selected.length > 0 ? selected : people;
                const nextCustom = Object.fromEntries(base.map((name) => [name, even]));
                onChange({ ...value, amount: nextAmount, customAmounts: nextCustom });
              }}
              onBlur={(e) => {
                // Evaluate math expression on blur
                const evaluated = evaluateMathExpression(e.target.value);
                if (evaluated != null) {
                  const formatted = round2(evaluated).toFixed(2);
                  if (!value.autoSplit) {
                    onChange({ ...value, amount: formatted });
                  } else {
                    const count = splitCount > 0 ? splitCount : 0;
                    const even = count > 0 ? round2(evaluated / count) : 0;
                    const base = selected.length > 0 ? selected : people;
                    const nextCustom = Object.fromEntries(base.map((name) => [name, even]));
                    onChange({ ...value, amount: formatted, customAmounts: nextCustom });
                  }
                }
              }}
              placeholder={`Amount (${expCurrency})`}
              className="h-10 w-full"
            />
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={expCurrency}
              onChange={(e) => onChange({ ...value, currency: e.target.value })}
              className="h-10 w-full sm:w-[130px] rounded-xl border pl-3 pr-10 text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: colors.border, background: colors.bg, color: colors.text }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        {expCurrency !== tripCurrency && amt > 0 && (
          <p className="mt-1 text-xs" style={{ color: colors.muted }}>
            ≈ {tripCurrency} {amtInTrip.toFixed(2)}
          </p>
        )}
      </div>

      {people.length === 0 ? (
        <p className="mb-3 text-sm" style={{ color: colors.muted }}>
          Add people first to split expenses.
        </p>
      ) : (
        <div className="mb-3 flex flex-col gap-4">
          {renderPaidBy(selected.length > 0 ? selected : people)}
          <div className="min-w-[240px] flex-1">
            <div className="mb-2">
              <p className="text-sm" style={{ color: colors.muted }}>
                Split between
              </p>
            </div>
            {customTaxMode ? (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium" style={{ color: colors.muted }}>
                      Tax ({expCurrency})
                    </label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={value.taxAmount === 0 || value.taxAmount === '0' ? '' : (value.taxAmount ?? '')}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                          onChange({ ...value, taxAmount: raw === '' ? 0 : raw });
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(String(e.target.value).replace(/,/g, ''));
                        onChange({ ...value, taxAmount: Number.isFinite(val) ? round2(val) : 0 });
                      }}
                      className="w-full text-right"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium" style={{ color: colors.muted }}>
                      Tip ({expCurrency})
                    </label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={value.tipAmount === 0 || value.tipAmount === '0' ? '' : (value.tipAmount ?? '')}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                          onChange({ ...value, tipAmount: raw === '' ? 0 : raw });
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(String(e.target.value).replace(/,/g, ''));
                        onChange({ ...value, tipAmount: Number.isFinite(val) ? round2(val) : 0 });
                      }}
                      className="w-full text-right"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {items.map((item) => {
                    const itemAmount = round2(Number(item.amount) || 0);
                    const itemPeople = item.splitBetween || [];
                    const totalSubtotal = items.reduce((sum, i) => sum + round2(Number(i.amount) || 0), 0);
                    const itemTaxTip = totalSubtotal > 0 && itemPeople.length > 0 ? round2((itemAmount / totalSubtotal) * totalTaxTip) : 0;
                    const taxTipPerPerson = itemPeople.length > 0 ? round2(itemTaxTip / itemPeople.length) : 0;
                    const amountPerPerson = itemPeople.length > 0 ? round2(itemAmount / itemPeople.length) : 0;
                    
                    return (
                      <div key={item.id} className="rounded-xl border p-3" style={{ borderColor: colors.border, background: colors.card }}>
                        <div className="flex items-center gap-2 mb-2">
                          <Input
                            type="text"
                            value={item.name || ''}
                            onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                            placeholder="Item name"
                            className="flex-1 text-xs"
                          />
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={item.amount || ''}
                            onChange={(e) => updateItem(item.id, 'amount', e.target.value)}
                            placeholder="Amount"
                            className="w-24 text-right text-xs"
                            onBlur={(e) => {
                              // Try to evaluate as math expression
                              const evaluated = evaluateMathExpression(e.target.value);
                              if (evaluated != null) {
                                updateItem(item.id, 'amount', round2(evaluated).toFixed(2));
                              } else {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) {
                                  updateItem(item.id, 'amount', round2(val).toFixed(2));
                                }
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="text-2xl px-2 text-red-500 hover:opacity-80 font-bold leading-none"
                          >
                            ×
                          </button>
                        </div>
                        <div className="mb-2">
                          <p className="text-xs mb-1" style={{ color: colors.muted }}>Split between:</p>
                          <div className="flex flex-wrap gap-2">
                            {people.map((p) => {
                              const isChecked = itemPeople.includes(p);
                              const personGroup = groups.find((g) => g.people.includes(p));
                              return (
                                <label
                                  key={p}
                                  className="flex items-center gap-1 rounded-full border px-2 py-1 text-xs cursor-pointer transition"
                                  style={{
                                    borderColor: isChecked ? colors.primary : colors.border,
                                    background: isChecked ? colors.card : colors.bg,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => togglePersonOnItem(item.id, p)}
                                    className="sr-only"
                                  />
                                  {personGroup && (
                                    <GroupTooltip groupMembers={personGroup.people} colors={colors}>
                                      <span className="material-symbols-outlined text-xs" style={{ color: colors.primary }}>
                                        group
                                      </span>
                                    </GroupTooltip>
                                  )}
                                  <span>{p}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        {itemAmount > 0 && (
                          <div className="pt-2 border-t text-xs space-y-1" style={{ borderColor: colors.border }}>
                            <div className="flex justify-between">
                              <span style={{ color: colors.muted }}>Item total:</span>
                              <span style={{ color: colors.text }}>{expCurrency} {itemAmount.toFixed(2)}</span>
                            </div>
                            {itemPeople.length > 0 && (
                              <div className="flex justify-between">
                                <span style={{ color: colors.muted }}>Per person:</span>
                                <span style={{ color: colors.text }}>{expCurrency} {amountPerPerson.toFixed(2)}</span>
                              </div>
                            )}
                            {totalTaxTip > 0 && (
                              <>
                                <div className="flex justify-between">
                                  <span style={{ color: colors.muted }}>Item tax/tip:</span>
                                  <span style={{ color: colors.text }}>{expCurrency} {itemTaxTip.toFixed(2)}</span>
                                </div>
                                {itemPeople.length > 0 && (
                                  <div className="flex justify-between">
                                    <span style={{ color: colors.muted }}>Tax/tip per person:</span>
                                    <span style={{ color: colors.text }}>{expCurrency} {taxTipPerPerson.toFixed(2)}</span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={addItem}
                    className="w-full text-xs px-3 py-2 rounded border transition hover:opacity-80"
                    style={{ borderColor: colors.border, color: colors.primary, background: colors.bg }}
                  >
                    + Add item
                  </button>
                </div>
                <div className="space-y-2 pt-3 border-t" style={{ borderColor: colors.border }}>
                  <p className="text-sm font-semibold" style={{ color: colors.text }}>Per person totals:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(selected.length > 0 ? selected : people).map((p) => {
                    const subtotal = personSubtotals[p] || 0;
                    const taxShare = personTaxShares[p] || 0;
                    const total = personTotals[p] || 0;
                    const personGroup = groups.find((g) => g.people.includes(p));
                    return (
                      <div key={p} className="rounded-lg border p-2 text-xs" style={{ borderColor: colors.border, background: colors.bg }}>
                        <div className="flex items-center gap-2 mb-1">
                          {personGroup && (
                            <GroupTooltip groupMembers={personGroup.people} colors={colors}>
                              <span className="material-symbols-outlined text-xs" style={{ color: colors.primary }}>
                                group
                              </span>
                            </GroupTooltip>
                          )}
                          <span className="font-semibold" style={{ color: colors.text }}>{p}</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span style={{ color: colors.muted }}>Subtotal:</span>
                            <span style={{ color: colors.text }}>{expCurrency} {subtotal.toFixed(2)}</span>
                          </div>
                          {totalTaxTip > 0 && (
                            <div className="flex justify-between">
                              <span style={{ color: colors.muted }}>Tax/Tip:</span>
                              <span style={{ color: colors.text }}>{expCurrency} {taxShare.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold">
                            <span style={{ color: colors.text }}>Total:</span>
                            <span style={{ color: colors.text }}>{expCurrency} {total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-bold" style={{ color: colors.muted }}>
                    {amt > 0 ? (
                      <>
                        {round2(amt - customSum).toFixed(2)} {expCurrency} left
                      </>
                    ) : (
                      'Enter expense amount'
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={toggleCustomTax}
                    className="px-3 py-2 text-sm font-semibold transition hover:opacity-80"
                    style={{ color: colors.primary }}
                  >
                    Back to normal split
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="rounded-2xl border p-3" style={{ borderColor: colors.border, background: colors.bg }}>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {people.map((p) => {
                      const isChecked = selected.includes(p);
                      const inputValue = (value.customAmounts || {})[p];
                      const personGroup = groups.find((g) => g.people.includes(p));
                      return (
                        <label
                          key={p}
                          className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm transition"
                      style={{
                        borderColor: colors.border,
                        background: isChecked ? colors.card : colors.bg,
                      }}
                        >
                          <span className="flex items-center gap-2">
                            <input type="checkbox" checked={isChecked} onChange={() => togglePerson(p)} />
                            {personGroup && (
                              <GroupTooltip groupMembers={personGroup.people} colors={colors}>
                                <span className="material-symbols-outlined text-xs" style={{ color: colors.primary }}>
                                  group
                                </span>
                              </GroupTooltip>
                            )}
                            <span>{p}</span>
                          </span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={isChecked ? (inputValue != null ? round2(inputValue).toFixed(2) : '') : ''}
                            onChange={(e) => setCustomAmount(p, e.target.value)}
                            onBlur={(e) => {
                              // Try to evaluate as math expression or percentage, then format
                              const inputStr = String(e.target.value).trim();
                              let val = null;
                              
                              if (inputStr.endsWith('%')) {
                                const percentStr = inputStr.slice(0, -1).replace(/,/g, '');
                                const percent = parseFloat(percentStr);
                                if (!isNaN(percent) && amt > 0) {
                                  val = round2((percent / 100) * amt);
                                }
                              } else {
                                const evaluated = evaluateMathExpression(inputStr);
                                val = evaluated != null ? round2(evaluated) : (isNaN(parseFloat(inputStr)) ? null : round2(parseFloat(inputStr)));
                              }
                              
                              if (val != null) {
                                setCustomAmount(p, round2(val).toFixed(2));
                              }
                            }}
                            disabled={!isChecked}
                            placeholder={isChecked ? `${expCurrency} or %` : ''}
                            className="w-24 text-right text-xs"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs" style={{ color: colors.muted }}>
                  <span>Leave all unchecked to split evenly among everyone.</span>
                  <div className="flex items-center gap-2">
                    {!value.autoSplit && (
                      <span className={customValid ? '' : 'font-medium'} style={{ color: customValid ? colors.muted : colors.accent }}>
                        Sum: {expCurrency} {customSum.toFixed(2)} {!customValid && '(must match total)'}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={toggleCustomTax}
                      className="px-3 py-2 text-sm font-semibold transition hover:opacity-80"
                      style={{ color: colors.primary }}
                    >
                      Custom tax
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="mb-2 block text-sm font-medium" style={{ color: colors.muted }}>
          Notes
        </label>
        <textarea
          value={value.notes || ''}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
          placeholder="Add any notes about this expense..."
          rows={3}
          className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 resize-none"
          style={{ borderColor: colors.border, background: colors.bg, color: colors.text }}
        />
      </div>

      <div className="flex gap-2">
        <Button
          onClick={onSave}
          disabled={!value.name.trim() || !amt || amt <= 0 || people.length === 0 || !customValid}
        >
          {submitLabel || 'Add expense'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
